import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';

/** One item in an entity's existing published gallery. */
interface GalleryItem {
  id: string;
  order?: number | null;
  creditName?: string | null;
  media?: {
    id: string;
    fileName?: string;
    url?: string;
  } | null;
}

interface ExistingGalleryCardProps {
  /** The kind of entity the photo was submitted for (e.g. 'attraction'). */
  entityType: string;
  /** The id of that entity. */
  entityId: string;
  /** Human-readable entity name for the heading, when known. */
  entityName?: string | null;
}

/**
 * Shows the target entity's existing gallery so a reviewer can compare a
 * submitted photo against what's already published — to spot duplicates or
 * judge whether the new shot adds value (the SUP-9 "lots of duplicates" pain).
 *
 * Only attractions currently have galleries, so this renders nothing for other
 * entity types.
 *
 * @param props - {@link ExistingGalleryCardProps}.
 */
export function ExistingGalleryCard({
  entityType,
  entityId,
  entityName,
}: ExistingGalleryCardProps) {
  const apiClient = useApiClient();
  const enabled = entityType === 'attraction' && Boolean(entityId);

  const { data, isLoading } = useQuery({
    queryKey: ['attraction-gallery', entityId],
    enabled,
    queryFn: async () => {
      const response = (await apiClient.get(`/attractions/${entityId}/gallery`, {
        params: { limit: 100 },
      })) as { data: GalleryItem[] };
      return response.data ?? [];
    },
  });

  if (!enabled) return null;

  const items = data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Existing Gallery{entityName ? ` — ${entityName}` : ''}
          {items.length > 0 ? ` (${items.length})` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading gallery…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This attraction has no published photos yet.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {items.map(item =>
              item.media?.url ? (
                <img
                  key={item.id}
                  src={item.media.url}
                  alt={item.creditName || 'Gallery photo'}
                  className="aspect-square w-full rounded-md object-cover"
                />
              ) : null,
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
